/* ============================================================================
   cl-quickadd.js — one-tap add to cart from a product card
   ----------------------------------------------------------------------------
   Loaded ONCE (deferred) from layout/theme.liquid on every page, and a no-op on a page with
   no cards: everything is delegated off document, so a card rendered later — the wishlist's
   Swym-built grid, the Algolia search hits injected by cl-algolia.js — is driven by this same
   code with no setup of its own.

   The markup comes from snippets/cl-quick-add.liquid (and, on search, from cl-algolia.js
   building the identical element): an ICON beside the wishlist heart, in the corner of the
   photo, sharing the heart's box. Config comes from window.CL_QUICKADD, set inline by the
   layout, because a static asset cannot read Liquid.

   WHY $360.addToCart AND NOT fetch('/cart/add.js'): that helper is the agency's, it is what
   the PDP button calls, and it is what keeps the header count, the drawer and the currency app
   in step. Going around it would mean reimplementing all three.

   IT IS `$360`, NOT `window.$360`, AND THAT IS THE WHOLE REASON THIS DID NOT WORK AT FIRST.
   vendor.min.js declares it as `const $360 = {…}` at the top level of a classic script, so the
   binding lives in the global LEXICAL scope and is never a property of the global OBJECT —
   window.$360 is undefined. The PDP's inline onclick gets away with `$360.addToCart(…)` because
   an inline handler is compiled in global scope and can see the lexical binding; a check on
   window.$360 simply failed, the handler returned, and a tap did nothing at all. The bare
   identifier behind a typeof guard is what reaches it.

   WE DO NOT PASS `button` TO IT, deliberately. Given one, the helper REPLACES the button's
   innerHTML with its own `<span class="btn-loading">`, pins the width and height inline, and
   restores all of it a second later — which would tear out the masked glyph this button is made
   of and fight the pending state below. Our own spinner already covers the wait.

   ONE-OFF STOCK. Every piece in this catalogue is a single item, so a variant already in the
   cart cannot be added again — there is no quantity stepper anywhere to correct it with. The
   button therefore latches into an "in cart" state and stays there, and the ids already in the
   cart arrive in the config so a page load starts in the right state.
   ============================================================================ */
(function () {
  'use strict';

  var CFG = window.CL_QUICKADD || {};
  var SEL = '[data-cl-qa]';

  /* Variant ids known to be in the cart. Seeded from the config, then kept up to date by our
     own adds — the alternative is re-reading /cart.js after every add, which is a round trip
     to learn something we just did. */
  var inCart = {};
  (CFG.inCart || []).forEach(function (id) { inCart[Number(id)] = true; });

  /* The button carries no visible words, so the NAME is the whole of what a shopper and a screen
     reader are told — both have to move with the state, or the tooltip still offers "Add to
     cart" on a card whose item is already in the cart. aria-label keeps the product title after
     the colon, since a grid of these would otherwise read as twenty identical buttons. */
  function label(btn, which) {
    var text = btn.getAttribute('data-label-' + which) || btn.getAttribute('data-label') || '';
    btn.setAttribute('title', text);
    var aria = btn.getAttribute('aria-label') || '';
    var sep = aria.indexOf(':');
    btn.setAttribute('aria-label', sep > -1 ? text + aria.slice(sep) : text);
  }

  /* `$360` is a top-level const in vendor.min.js — see the note above. typeof keeps this safe
     on a page where that script failed to load at all. */
  function api() {
    if (typeof $360 !== 'undefined' && $360 && $360.addToCart) return $360;
    if (window.$360 && window.$360.addToCart) return window.$360;
    return null;
  }

  /* `burst` is the tick's entrance, and it is a SEPARATE, TRANSIENT class for a reason: this
     same function runs on page load for every card whose piece is already in the cart, and a
     grid of ticks all popping at once would read as twenty things having just happened. */
  function setAdded(btn, burst) {
    btn.classList.add('is-added');
    btn.classList.remove('is-adding');
    btn.removeAttribute('aria-busy');
    btn.disabled = true;
    label(btn, 'added');
    if (!burst) return;
    btn.classList.add('is-just-added');
    setTimeout(function () { btn.classList.remove('is-just-added'); }, 700);
  }

  /** Paints the remembered cart state onto every quick-add button in `scope`. */
  function sync(scope) {
    var nodes = (scope || document).querySelectorAll(SEL);
    for (var i = 0; i < nodes.length; i++) {
      var id = Number(nodes[i].getAttribute('data-variant-id'));
      if (inCart[id] && !nodes[i].classList.contains('is-added')) setAdded(nodes[i]);
    }
  }

  function fail(btn) {
    /* Handed straight back: the cart did not change, so a retry is the right next move. */
    btn.classList.remove('is-adding');
    btn.removeAttribute('aria-busy');
    btn.disabled = false;
    label(btn, 'label');
  }

  /* CAPTURE PHASE, and it is load-bearing on the Algolia search page. Delegating from document
     in the bubble phase means every handler between the button and document has ALREADY run by
     the time we get the event — and Algolia's hit card carries its own click handler that
     navigates to the product. Tapping quick add there added the item and then left the page.
     In capture we are first, so stopPropagation stops that handler from ever seeing the click.
     Same reason the filter button in cl-algolia.js is bound in capture.

     preventDefault covers the other route to the same place: on surfaces where the button sits
     inside the card's <a>, the anchor's own default would follow the link. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest(SEL) : null;
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    if (btn.disabled || btn.classList.contains('is-adding') || btn.classList.contains('is-added')) return;

    var id = Number(btn.getAttribute('data-variant-id'));
    if (!id) return;

    var a = api();
    if (!a) return;

    btn.classList.add('is-adding');
    btn.setAttribute('aria-busy', 'true');
    label(btn, 'adding');

    /* The icon flies to the header cart, the same motion the PDP uses — clMotion owns it so
       both surfaces cannot drift apart. It runs on the click, because it is the answer to the
       tap; a failed add is reported by the button coming back. */
    if (CFG.fly !== false && window.clMotion && window.clMotion.flyToCart) {
      window.clMotion.flyToCart(btn, { icon: CFG.icon, color: CFG.flyColor, size: CFG.flySize, ms: CFG.flyMs });
    }

    var settled = false;
    var done = function (cart) {
      if (settled) return;
      settled = true;
      inCart[id] = true;
      setAdded(btn, true);
      /* Same variant, other surfaces: the piece can appear in a rail and a grid on one page. */
      sync();
      if (CFG.openDrawer !== false && a.showHeaderCart) {
        a.showHeaderCart({ update: true });
      } else if (cart && a.updateHeaderCartCount) {
        /* With the drawer suppressed nothing else would refresh the header's count, and a cart
           icon still reading the old number is the one thing that makes a silent add feel
           broken. The helper hands us the new cart, so no second request is needed. */
        a.updateHeaderCartCount(cart);
      }
    };

    try {
      /* No `button` — see the note at the top of this file. */
      a.addToCart({ qty: 1, variantId: id }, done);
    } catch (err) {
      fail(btn);
      return;
    }

    /* The helper takes a callback and does not report failure any other way, so a request that
       never comes back would leave the button spinning for good. 15s, the same release the
       cart drawer's remove uses. */
    setTimeout(function () {
      if (!settled && btn.classList.contains('is-adding')) {
        settled = true;
        fail(btn);
      }
    }, 15000);
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { sync(); });
  else sync();

  /* Cards built after this ran (Algolia hits, the wishlist grid) call sync themselves. */
  window.clQuickAdd = { sync: sync, inCart: inCart };
})();
